import { useCallback, useState } from 'react';
import { BraindayAPI, BraindayError } from '@/services/BraindayAPI';
import { useTaskStore } from '@/stores/taskStore';
import { selectEnergyPattern, useUserPatternStore } from '@/stores/userPatternStore';
import { toDateKey } from '@/lib/dateKey';

export function useDayBuilder() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<BraindayError | null>(null);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const tasks = useTaskStore
        .getState()
        .tasks.filter((t) => t.status === 'inbox' || t.status === 'deferred');

      const energyPattern = selectEnergyPattern(useUserPatternStore.getState());

      // Derive the "today vs tomorrow" cutoff from the user's chronotype: once
      // we're past the last high-energy hour by 2h (a wind-down buffer), the
      // productive part of today is gone — plan tomorrow instead. Falls back
      // to a hard 20:00 cutoff if highHours is empty.
      const now = new Date();
      const lastPeak =
        energyPattern.highHours.length > 0 ? Math.max(...energyPattern.highHours) : 18;
      const switchToTomorrow = now.getHours() >= lastPeak + 2;
      const targetDay = switchToTomorrow ? new Date(now.getTime() + 86_400_000) : now;
      const targetDate = toDateKey(targetDay);

      // When planning today AND the day is already partway through, tell the
      // AI the earliest allowed start so it doesn't pick a past hour.
      const earliestStartMs = switchToTomorrow ? undefined : now.getTime();

      const { schedule } = await BraindayAPI.schedule({
        tasks: tasks.map((t) => ({
          id: t.id,
          title: t.title,
          durationMinutes: t.durationMinutes,
          priority:
            t.priority === 'critical' || t.priority === 'high'
              ? 'high'
              : t.priority === 'low'
                ? 'low'
                : 'med',
        })),
        events: [],
        energyPattern,
        targetDate,
        // getTimezoneOffset() so the server can anchor targetDate's LOCAL day.
        tzOffsetMinutes: now.getTimezoneOffset(),
        ...(earliestStartMs !== undefined ? { earliestStartMs } : {}),
      });

      const { scheduleTask, setMicroStep } = useTaskStore.getState();
      // Re-anchor the AI's day/month/year to today's targetDate, preserving
      // only the hour/minute it chose. Defends against model "year drift"
      // (returning a previous year's timestamp despite the explicit targetDate).
      const [anchorYear, anchorMonth, anchorDay] = targetDate.split('-').map(Number) as [
        number,
        number,
        number,
      ];
      const reanchor = (ms: number): Date => {
        const d = new Date(ms);
        d.setFullYear(anchorYear, anchorMonth - 1, anchorDay);
        return d;
      };
      for (const slot of schedule) {
        scheduleTask(slot.taskId, reanchor(slot.startMs), reanchor(slot.endMs));
      }

      // Fire-and-forget micro-step calls in the background.
      for (const slot of schedule) {
        const task = useTaskStore.getState().tasks.find((x) => x.id === slot.taskId);
        if (!task) continue;
        void BraindayAPI.microStep({
          taskTitle: task.title,
          durationMinutes: task.durationMinutes,
          ...(task.notes !== undefined ? { taskDescription: task.notes } : {}),
        })
          .then((micro) => {
            setMicroStep(task.id, micro);
          })
          .catch((e) => {
            console.warn('micro-step failed (background)', e);
          });
      }

      setLoading(false);
    } catch (e) {
      const err = e instanceof BraindayError ? e : new BraindayError('unknown', String(e));
      setError(err);
      setLoading(false);
      throw err;
    }
  }, []);

  return { run, loading, error };
}
